import { useEffect, useRef } from 'react';
import { trackPageExit, trackInactivity } from '../utils/tagManager';

/**
 * Hook pour suivre les sorties de page et l'inactivité des utilisateurs
 * @param {Object} options - Options de configuration
 * @param {number} options.inactivityTimeout - Délai en ms avant de considérer l'utilisateur comme inactif (défaut: 60000ms)
 * @param {boolean} options.trackBrowserClose - Suivre la fermeture du navigateur (défaut: true)
 * @param {boolean} options.trackNavigation - Suivre la navigation entre les pages (défaut: true)
 */
const useExitTracking = (options = {}) => {
  const {
    inactivityTimeout = 60000, // 60 secondes par défaut
    trackBrowserClose = true,
    trackNavigation = true
  } = options || {};

  // Référence pour stocker l'heure d'arrivée sur la page
  const pageEntryTime = useRef(Date.now());
  // Référence pour le timer d'inactivité
  const inactivityTimer = useRef(null);
  // Référence pour savoir si l'utilisateur est considéré comme inactif
  const isInactive = useRef(false);

  useEffect(() => {
    // Réinitialiser le temps d'entrée à chaque montage du composant
    pageEntryTime.current = Date.now();
    isInactive.current = false;

    // Fonction pour calculer le temps passé sur la page
    const getTimeOnPage = () => {
      return Math.round((Date.now() - pageEntryTime.current) / 1000);
    };

    // Fonction pour suivre l'inactivité
    const handleInactivity = () => {
      isInactive.current = true;
      const inactiveTime = getTimeOnPage();
      trackInactivity(inactiveTime);
    };

    // Fonction pour réinitialiser le timer d'inactivité
    const resetInactivityTimer = () => {
      if (isInactive.current) {
        isInactive.current = false;
      }
      
      if (inactivityTimer.current) {
        clearTimeout(inactivityTimer.current);
      }
      
      inactivityTimer.current = setTimeout(handleInactivity, inactivityTimeout);
    };

    // Événements utilisateur qui réinitialisent le timer d'inactivité
    const userActivityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    
    // Ajouter les écouteurs d'événements pour l'activité utilisateur
    userActivityEvents.forEach(event => {
      window.addEventListener(event, resetInactivityTimer);
    });
    
    // Démarrer le timer d'inactivité
    resetInactivityTimer();

    // Fonction pour suivre la sortie de page
    const handlePageExit = (exitType, exitTarget = null) => {
      const timeOnPage = getTimeOnPage();
      trackPageExit(exitType, timeOnPage, exitTarget);
    };

    // Suivre la fermeture du navigateur
    const handleBeforeUnload = (e) => {
      if (trackBrowserClose) {
        handlePageExit('browser_close');
      }
    };

    // Suivre la navigation entre les pages
    const handleNavigation = (e) => {
      if (trackNavigation) {
        // Obtenir l'URL de destination
        const exitTarget = e.currentTarget.href || null;
        handlePageExit('navigation', exitTarget);
      }
    };

    // Ajouter les écouteurs d'événements pour les sorties de page
    if (trackBrowserClose) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    // Ajouter des écouteurs d'événements pour les liens internes
    if (trackNavigation) {
      const internalLinks = document.querySelectorAll('a[href^="/"]');
      internalLinks.forEach(link => {
        link.addEventListener('click', handleNavigation);
      });
    }

    // Nettoyage lors du démontage du composant
    return () => {
      // Nettoyer le timer d'inactivité
      if (inactivityTimer.current) {
        clearTimeout(inactivityTimer.current);
      }

      // Supprimer les écouteurs d'événements pour l'activité utilisateur
      userActivityEvents.forEach(event => {
        window.removeEventListener(event, resetInactivityTimer);
      });

      // Supprimer les écouteurs d'événements pour les sorties de page
      if (trackBrowserClose) {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      }

      // Supprimer les écouteurs d'événements pour les liens internes
      if (trackNavigation) {
        const internalLinks = document.querySelectorAll('a[href^="/"]');
        internalLinks.forEach(link => {
          link.removeEventListener('click', handleNavigation);
        });
      }

      // Enregistrer la sortie de page lors du démontage du composant
      handlePageExit('page_change');
    };
  }, [inactivityTimeout, trackBrowserClose, trackNavigation]);
};

export default useExitTracking;